/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableRow,
  TableBody,
  TableCell,
  Typography,
  TableContainer,
  Box,
  Pagination,
  IconButton,
  Tooltip,
} from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import PhoneCallbackOutlinedIcon from '@mui/icons-material/PhoneCallbackOutlined';
import Scrollbar from '../../../components/scrollbar/Scrollbar';
import ClientHeadTable from '../../../components/clientHeadTable/ClientHeadTable';
import Label from '../../../components/label';
import PropertyRequestDetailsModal from './PropertyRequestDetailsModal';

export default function PropertyRequestsTable({
  requests,
  handleChangePage,
  page,
  rowsPerPage = 10,
  areaLookupsMap = {},
  budgetLookupsMap = {},
  roomCountLookupsMap = {},
  sortType = 'ASC',
  onSortChange,
  onStatusUpdated,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';

  const [selectedRequest, setSelectedRequest] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  const handleOpenDetails = (row) => {
    setSelectedRequest(row);
    setOpenModal(true);
  };

  const handleCloseDetails = () => {
    setOpenModal(false);
    setSelectedRequest(null);
  };

  const TABLE_HEAD = [
    {
      id: 'requestNumber',
      label: t('propertyRequests.requestNumber'),
      alignRight: isRtl,
    },
    {
      id: 'phone',
      label: t('propertyRequests.phone'),
      alignRight: isRtl,
    },
    {
      id: 'purpose',
      label: t('propertyRequests.requestType'),
      alignRight: isRtl,
    },
    {
      id: 'propertyType',
      label: t('propertyRequests.propertyType'),
      alignRight: isRtl,
    },
    {
      id: 'coordinate',
      label: t('propertyRequests.coordinate'),
      alignRight: isRtl,
    },
    {
      id: 'budget',
      label: t('propertyRequests.budget'),
      alignRight: isRtl,
    },
    {
      id: 'status',
      label: t('propertyRequests.status'),
      alignCenter: true,
    },
    {
      id: 'action',
      label: t('propertyRequests.actions'),
      alignCenter: true,
    },
  ];

  const handleRequestSort = () => {
    if (onSortChange) {
      onSortChange(sortType === 'ASC' ? 'DESC' : 'ASC');
    }
  };

  const content = requests?.content || [];
  const totalElements = requests?.totalElements || 0;
  const totalPages = requests?.totalPages || 0;

  return (
    <>
      <Scrollbar>
        <TableContainer sx={{ minWidth: 900, borderRadius: '12px', border: '1px solid #E2E8F0' }}>
          <Table>
            <ClientHeadTable
              headLabel={TABLE_HEAD}
              order={sortType ? sortType.toLowerCase() : 'asc'}
              orderBy="requestNumber"
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {content.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={TABLE_HEAD.length} align="center" sx={{ py: 6 }}>
                    <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                      {t('propertyRequests.noData')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                content.map((row) => {
                  const {
                    id,
                    requestNumber,
                    user,
                    coordinate,
                    purpose,
                    unitType,
                    budget,
                    status,
                  } = row;

                  const isCompleted = status === 'COMPLETED';
                  const budgetLabel = budgetLookupsMap[budget] || budget || '---';

                  return (
                    <TableRow
                      hover
                      key={id || requestNumber}
                      tabIndex={-1}
                      sx={{
                        '&:hover': {
                          backgroundColor: '#F8FAFC !important',
                        },
                      }}
                    >
                      {/* Request Number */}
                      <TableCell align={isRtl ? 'right' : 'left'}>
                        <Box
                          sx={{
                            display: 'inline-block',
                            px: 1.25,
                            py: 0.5,
                            borderRadius: '6px',
                            backgroundColor: '#EEF2F6',
                            color: '#1E293B',
                            fontWeight: 700,
                            fontSize: '13px',
                          }}
                        >
                          #{requestNumber || id}
                        </Box>
                      </TableCell>

                      {/* Phone */}
                      <TableCell align={isRtl ? 'right' : 'left'}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#475569',
                            direction: 'ltr',
                            textAlign: isRtl ? 'right' : 'left',
                            fontWeight: 500,
                          }}
                        >
                          {user?.phone || '---'}
                        </Typography>
                      </TableCell>

                      {/* Request Type */}
                      <TableCell align={isRtl ? 'right' : 'left'}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                          {purpose === 'RESIDENTIAL'
                            ? t('propertyRequests.residential')
                            : purpose === 'COMMERCIAL'
                              ? t('propertyRequests.commercial')
                              : '---'}
                        </Typography>
                      </TableCell>

                      {/* Property Type */}
                      <TableCell align={isRtl ? 'right' : 'left'}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                            {unitType?.name || '---'}
                          </Typography>

                        </Box>
                      </TableCell>

                      {/* Coordinate / Company */}
                      <TableCell align={isRtl ? 'right' : 'left'}>
                        <Typography variant="body2" sx={{ color: '#475569', fontWeight: 500 }}>
                          {coordinate?.name || '---'}
                        </Typography>
                      </TableCell>



                      {/* Budget */}
                      <TableCell align={isRtl ? 'right' : 'left'}>
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            px: 1.25,
                            py: 0.4,
                            borderRadius: '6px',
                            backgroundColor: '#EFF6FF',
                            border: '1px solid #DBEAFE',
                            color: '#1D4ED8',
                            fontWeight: 600,
                            fontSize: '12px',
                          }}
                        >
                          {budgetLabel}
                        </Box>
                      </TableCell>

                      {/* Status */}
                      <TableCell align="center">
                        <Label
                          variant="soft"
                          color={isCompleted ? 'success' : 'warning'}
                          sx={{
                            fontWeight: 700,
                            fontSize: '11px',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: '6px',
                          }}
                        >
                          {isCompleted
                            ? t('propertyRequests.completed')
                            : t('propertyRequests.pending')}
                        </Label>
                      </TableCell>

                      {/* Action */}
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                          {!isCompleted && (
                            <Tooltip title={t('propertyRequests.markAsContacted')}>
                              <IconButton
                                onClick={() => handleOpenDetails(row)}
                                sx={{
                                  backgroundColor: '#E7F7EF',
                                  color: '#0CAF60',
                                  width: 36,
                                  height: 36,
                                  '&:hover': {
                                    backgroundColor: '#D1F2E2',
                                  },
                                }}
                              >
                                <PhoneCallbackOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title={t('propertyRequests.viewDetails')}>
                            <IconButton
                              onClick={() => handleOpenDetails(row)}
                              sx={{
                                backgroundColor: '#F1F5F9',
                                color: '#0F172A',
                                width: 36,
                                height: 36,
                                '&:hover': {
                                  backgroundColor: '#E2E8F0',
                                },
                              }}
                            >
                              <VisibilityOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Scrollbar>

      {/* Pagination Footer */}
      {totalPages > 0 && (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
            padding: '16px 8px',
            mt: 2,
          }}
        >
          <Pagination
            count={totalPages}
            page={page + 1}
            onChange={handleChangePage}
            color="primary"
            shape="rounded"
          />
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            {t('General.showing')} {page * rowsPerPage + (content.length > 0 ? 1 : 0)}{' '}
            {t('General.of')} {Math.min((page + 1) * rowsPerPage, totalElements)}{' '}
            {t('General.to')} {totalElements} {t('General.entries')}
          </Typography>
        </Box>
      )}

      {/* Details Modal */}
      <PropertyRequestDetailsModal
        open={openModal}
        handleClose={handleCloseDetails}
        request={selectedRequest}
        areaLookupsMap={areaLookupsMap}
        budgetLookupsMap={budgetLookupsMap}
        roomCountLookupsMap={roomCountLookupsMap}
        onStatusUpdated={onStatusUpdated}
      />
    </>
  );
}

PropertyRequestsTable.propTypes = {
  requests: PropTypes.object,
  handleChangePage: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number,
  areaLookupsMap: PropTypes.object,
  budgetLookupsMap: PropTypes.object,
  roomCountLookupsMap: PropTypes.object,
  sortType: PropTypes.string,
  onSortChange: PropTypes.func,
  onStatusUpdated: PropTypes.func,
};
